import React from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { Box, Typography, Chip, Stack } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

interface Props {
  measurements: any[];
  studentName?: string;
}

const CumulativeTrendChart: React.FC<Props> = ({ measurements, studentName }) => {
  // 1. Sort measurements chronologically
  const sortedData = [...measurements].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  if (!sortedData || sortedData.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No data available for this student.
        </Typography>
      </Box>
    );
  }

  // 2. Format X-Axis categories and extract year list
  const categories = sortedData.map(m => {
    const d = new Date(m.date);
    if (isNaN(d.getTime())) return "Unknown Date";
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  });

  const years = Array.from(
    new Set(
      sortedData
        .map(m => {
          const d = new Date(m.date);
          return isNaN(d.getTime()) ? null : d.getFullYear();
        })
        .filter(Boolean)
    )
  );

  // 3. Series Data
  const heightData = sortedData.map(m => Number(m.height) || 0);
  const weightData = sortedData.map(m => Number(m.weight) || 0);
  const bmiData = sortedData.map(m => Number(m.bmi) || 0);
  const heartRateData = sortedData.map(m => Number(m.heartRate) || 0);

  const series = [
    {
      name: "Height (cm)",
      type: "line",
      data: heightData
    },
    {
      name: "Weight (kg)",
      type: "line",
      data: weightData
    },
    {
      name: "BMI",
      type: "line",
      data: bmiData
    },
    {
      name: "Heart Rate (bpm)",
      type: "line",
      data: heartRateData
    }
  ];

  const isSingleRecord = sortedData.length === 1;

  // 4. Chart Options
  const options: ApexOptions = {
    chart: {
      type: "line",
      stacked: false,
      fontFamily: "Inter, Roboto, 'Segoe UI', sans-serif",
      animations: {
        enabled: false
      },
      toolbar: {
        show: true,
        tools: {
          download: false,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true
        }
      },
      zoom: {
        enabled: true,
        type: "x"
      }
    },
    colors: ["#0284C7", "#10B981", "#F59E0B", "#EC4899"],
    stroke: {
      width: isSingleRecord ? [0, 0, 0, 0] : [3, 3, 3, 3],
      curve: "smooth"
    },
    markers: {
      size: isSingleRecord ? 9 : 6,
      strokeWidth: 2.5,
      strokeColors: "#FFFFFF",
      hover: {
        size: 10,
        sizeOffset: 2
      }
    },
    xaxis: {
      categories: categories,
      labels: {
        rotate: sortedData.length > 3 ? -35 : 0,
        rotateAlways: sortedData.length > 5,
        style: {
          fontSize: "11px",
          fontWeight: 500,
          colors: "#64748B"
        }
      },
      title: {
        text: "Measurement Timeline (Chronological)",
        style: {
          fontWeight: 600,
          color: "#1E293B",
          fontSize: "12px"
        }
      },
      axisBorder: { show: true, color: "#E2E8F0" },
      axisTicks: { show: true, color: "#E2E8F0" }
    },
    yaxis: [
      {
        seriesName: "Height (cm)",
        axisTicks: { show: true, color: "#0284C7" },
        axisBorder: { show: true, color: "#0284C7" },
        labels: {
          style: { colors: "#0284C7", fontWeight: 500, fontSize: "11px" },
          formatter: val => `${Math.round(val)} cm`
        },
        title: {
          text: "Height (cm) / Weight (kg)",
          style: { color: "#0284C7", fontWeight: 600, fontSize: "12px" }
        },
        min: 0,
        max: Math.max(220, Math.ceil(Math.max(...heightData, 100) * 1.1))
      },
      {
        seriesName: "Weight (kg)",
        opposite: false,
        show: false, // share left axis scale
        min: 0,
        max: Math.max(220, Math.ceil(Math.max(...heightData, 100) * 1.1))
      },
      {
        seriesName: "BMI",
        opposite: true,
        axisTicks: { show: true, color: "#F59E0B" },
        axisBorder: { show: true, color: "#F59E0B" },
        labels: {
          style: { colors: "#F59E0B", fontWeight: 500, fontSize: "11px" },
          formatter: val => `${val.toFixed(1)}`
        },
        title: {
          text: "BMI / Heart Rate (bpm)",
          style: { color: "#F59E0B", fontWeight: 600, fontSize: "12px" }
        },
        min: 0,
        max: Math.max(120, Math.ceil(Math.max(...heartRateData, ...bmiData, 50) * 1.15))
      },
      {
        seriesName: "Heart Rate (bpm)",
        opposite: true,
        show: false, // share right axis scale
        min: 0,
        max: Math.max(120, Math.ceil(Math.max(...heartRateData, ...bmiData, 50) * 1.15))
      }
    ],
    tooltip: {
      shared: true,
      intersect: false,
      custom: ({ seriesIndex, dataPointIndex, w }) => {
        const dateStr = categories[dataPointIndex] || "-";
        const h = heightData[dataPointIndex];
        const wt = weightData[dataPointIndex];
        const bmi = bmiData[dataPointIndex];
        const hr = heartRateData[dataPointIndex];

        return `
          <div style="padding: 12px 16px; font-family: Inter, Roboto, sans-serif; font-size: 12px; line-height: 1.6; background: rgba(255, 255, 255, 0.98); backdrop-filter: blur(8px); border: 1px solid #E2E8F0; border-radius: 10px; box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.12), 0 8px 10px -6px rgba(15, 23, 42, 0.08);">
            <div style="font-weight: 700; color: #0F172A; margin-bottom: 6px; border-bottom: 1px solid #F1F5F9; padding-bottom: 4px; display: flex; align-items: center; justify-content: space-between; gap: 12px;">
              <span>${studentName || "Student"}</span>
              <span style="font-size: 11px; font-weight: 500; color: #64748B;">${dateStr}</span>
            </div>
            <div style="display: flex; flex-direction: column; gap: 4px; margin-top: 4px;">
              <div style="display: flex; justify-content: space-between; align-items: center; gap: 16px;">
                <span style="display: flex; align-items: center; gap: 6px; color: #475569;">
                  <span style="width: 8px; height: 8px; border-radius: 50%; background: #0284C7; display: inline-block;"></span>
                  Height:
                </span>
                <b style="color: #0284C7;">${h} cm</b>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; gap: 16px;">
                <span style="display: flex; align-items: center; gap: 6px; color: #475569;">
                  <span style="width: 8px; height: 8px; border-radius: 50%; background: #10B981; display: inline-block;"></span>
                  Weight:
                </span>
                <b style="color: #10B981;">${wt} kg</b>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; gap: 16px;">
                <span style="display: flex; align-items: center; gap: 6px; color: #475569;">
                  <span style="width: 8px; height: 8px; border-radius: 50%; background: #F59E0B; display: inline-block;"></span>
                  BMI:
                </span>
                <b style="color: #F59E0B;">${bmi ? bmi.toFixed(2) : "-"}</b>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; gap: 16px;">
                <span style="display: flex; align-items: center; gap: 6px; color: #475569;">
                  <span style="width: 8px; height: 8px; border-radius: 50%; background: #EC4899; display: inline-block;"></span>
                  Heart Rate:
                </span>
                <b style="color: #EC4899;">${hr} bpm</b>
              </div>
            </div>
          </div>
        `;
      }
    },
    grid: {
      borderColor: "#F1F5F9",
      strokeDashArray: 3,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } }
    },
    legend: {
      position: "top",
      horizontalAlign: "center",
      fontSize: "12px",
      fontFamily: "Inter, Roboto, sans-serif",
      fontWeight: 500,
      itemMargin: { horizontal: 10, vertical: 4 }
    }
  };

  return (
    <Box sx={{ width: "100%", mt: 1 }}>
      {/* Header Info Bar */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{
          mb: 2,
          p: 1.5,
          bgcolor: "#F8FAFC",
          borderRadius: 2,
          border: "1px solid #E2E8F0"
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <TrendingUpIcon sx={{ color: "#0F172A" }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#0F172A" }}>
            Cumulative Multi-Year Health Overview
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          <Chip
            size="small"
            icon={<CalendarMonthIcon />}
            label={`Timeline: ${years.join(", ")} (${sortedData.length} records)`}
            variant="outlined"
            sx={{ borderColor: "#CBD5E1", color: "#334155", fontWeight: 500 }}
          />
        </Stack>
      </Stack>

      <ReactApexChart
        options={options}
        series={series}
        type="line"
        height={420}
      />
    </Box>
  );
};

export default CumulativeTrendChart;
